import liff from "@line/liff";
import { clsx } from "clsx";
import { motion } from "framer-motion";
import type { FormEvent } from "react";
import { useState } from "react";
import { getErrorMessage, registerQualification } from "@/api/client";
import type { MaritimeDepartment, MaritimeGrade } from "@/hooks/useMaritimeQualifications";
import { CheckCircleIcon } from "./icons/CheckCircleIcon";
import { BackButton } from "./ui/BackButton";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";
import { Input } from "./ui/Input";

type Props = {
  userId: string;
  department: MaritimeDepartment;
  grade: MaritimeGrade;
  topQualificationId: number;
  departmentNames: Record<MaritimeDepartment, string>;
  gradeNames: Record<MaritimeGrade, string>;
  onBack: () => void;
};

export const OralExamForm = ({
  userId,
  department,
  grade,
  topQualificationId,
  departmentNames,
  gradeNames,
  onBack,
}: Props) => {
  const [acquiredAt, setAcquiredAt] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const qualificationName = `海技士（${departmentNames[department]}）${gradeNames[grade]}`;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!acquiredAt) return;

    setIsSubmitting(true);
    setMessage(null);

    try {
      await registerQualification({
        userId,
        qualificationTypeId: topQualificationId,
        acquiredAt,
      });
      liff.closeWindow();
    } catch (error) {
      console.error("Failed to register qualification:", error);
      setMessage({ type: "error", text: getErrorMessage(error) });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <BackButton onClick={onBack} />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mb-6 flex items-center gap-3 rounded-xl border border-success-border bg-success-bg p-4"
      >
        <CheckCircleIcon />
        <div>
          <h3 className="font-semibold text-success-text">筆記試験合格済み</h3>
          <p className="mt-0.5 text-ocean-600 text-sm">口述試験に合格すると資格取得となります</p>
        </div>
      </motion.div>

      <form onSubmit={handleSubmit}>
        <h2 className="mb-2 font-semibold text-lg text-ocean-800">{qualificationName}</h2>
        <p className="mb-5 text-[13px] text-ocean-500">口述試験に合格した日を入力してください</p>

        <Input
          type="date"
          label="口述試験合格日"
          value={acquiredAt}
          onChange={(e) => setAcquiredAt(e.target.value)}
          required
          className="mb-5"
        />

        <Button
          type="submit"
          variant="line"
          fullWidth
          isLoading={isSubmitting}
          disabled={!acquiredAt}
        >
          資格取得を登録する
        </Button>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={clsx(
              "mt-4 rounded-xl border p-4 text-center",
              message.type === "success"
                ? "border-success-border bg-success-bg text-success-text"
                : "border-error-border bg-error-bg text-error-text",
            )}
          >
            {message.text}
          </motion.div>
        )}
      </form>
    </Card>
  );
};
